import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { useTranslation } from '../hooks/useTranslation';
import { Camera } from 'lucide-react';

interface CameraCaptureDialogProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  title?: string;
  description?: string;
  captureButtonText?: string;
  cancelButtonText?: string;
}

export const CameraCaptureDialog: React.FC<CameraCaptureDialogProps> = ({
  open,
  onClose,
  onCapture,
  title,
  description,
  captureButtonText,
  cancelButtonText,
}) => {
  const { translate } = useTranslation();
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Start camera when dialog opens
  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [open]);

  // Set video stream when available
  useEffect(() => {
    if (videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch((error) => {
        console.error('Error playing video:', error);
      });
    }
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [cameraStream]);

  const startCamera = async () => {
    try {
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      setCameraStream(stream);
    } catch (error) {
      console.error('Error accessing camera:', error);
      // Close dialog on error
      onClose();
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Set video stream when camera stream is available and dialog is open
  useEffect(() => {
    if (open && cameraStream && videoRef.current) {
      const video = videoRef.current;

      // Set the stream
      video.srcObject = cameraStream;

      // Play video when metadata is loaded
      const handleLoadedMetadata = () => {
        video.play().catch((error) => {
          console.error('Error playing video:', error);
        });
      };

      // Try to play immediately
      video.play().catch((error) => {
        // If play fails, wait for metadata
        video.addEventListener('loadedmetadata', handleLoadedMetadata, {
          once: true,
        });
      });

      // Also listen for loadedmetadata as backup
      video.addEventListener('loadedmetadata', handleLoadedMetadata, {
        once: true,
      });

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [open, cameraStream]);

  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context && video.videoWidth > 0 && video.videoHeight > 0) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], 'capture.jpg', {
                type: 'image/jpeg',
              });
              stopCamera();
              onCapture(file);
              onClose();
            }
          },
          'image/jpeg',
          0.9
        );
      }
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {title || translate('pos.common.camera_capture', 'Capture Photo')}
          </DialogTitle>
          <DialogDescription>
            {description ||
              translate(
                'pos.common.camera_instructions',
                'Position the item in front of the camera and click Capture when ready'
              )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative bg-black rounded-lg overflow-hidden min-h-[300px] flex items-center justify-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-auto max-h-[60vh]"
            />
            <canvas ref={canvasRef} className="hidden" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={handleClose}>
              {cancelButtonText || translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleCapture}
              className="flex items-center gap-2"
              disabled={!cameraStream}
            >
              <Camera className="h-4 w-4" />
              {captureButtonText || translate('pos.common.capture', 'Capture')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
